'use client';

import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiInformationLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { RiArrowRightUpLine } from '@/components/ui/rtl-icons';
import { Link } from '@/i18n/navigation';
import { helpLinkHref } from '@/lib/help/types';
import type { HelpBlock } from '@/lib/help/types';
import { spring, springFade, stagger } from '@/lib/motion';

/**
 * Yardım makalelerinin blok çizicisi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Deri `.art-*` — YENİ bir okuma yüzeyi YAZILMADI
 * ═══════════════════════════════════════════════════════════════════════════
 * Paragraf, başlık, liste ve not blog yazısının (`blog-article.tsx`) ve
 * yasal metnin (`legal-doc.tsx`) sınıflarını kullanıyor. Bu depodaki kural:
 * yapılmış bir yüzey varsa ikincisi yazılmaz. Üçü de uzun metin, üçü de aynı
 * ızgarada, üçü de aynı ölçüyü ve satır aralığını istiyor.
 *
 * ⚠️ Bedeli bir bağ: `.art-*` değişirse yardım merkezi de değişir. Bilinçli
 * — `globals.css` → "Yazı sayfası" bölümüne dokunan kişinin bilmesi gereken
 * üçüncü tüketici burası.
 *
 * Yalnızca BURAYA ait üç blok için yeni sınıf var (`globals.css` → "Yardım
 * merkezi"): adım dizisi, tanım listesi ve tek satırlık bağlantı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * `switch` tam kapsayıcı
 * ═══════════════════════════════════════════════════════════════════════════
 * `HelpBlock` birliğine yeni bir tip eklendiğinde TypeScript burada durur.
 * Bilinçli — ekranda karşılığı olmayan bir blok tipi metne giremesin.
 */

/* Adımlar sırayla oturuyor: dizi bir SIRA anlatıyor ve hareket tam olarak
   onu söylüyor. `whileInView` + `once: true` (DESIGN.md §1) — bölüm sayfanın
   ortasında olabiliyor, açılışta ekranda olmayabilir. */
const stepsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const stepV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

export function HelpBlocks({ blocks }: { blocks: readonly HelpBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </>
  );
}

function Block({ block }: { block: HelpBlock }) {
  switch (block.type) {
    case 'p':
      return <p className="art-p">{block.text}</p>;

    case 'h3':
      return <h3 className="art-h3">{block.text}</h3>;

    case 'list': {
      const items = block.items.map((item, index) => (
        <li className="art-li" key={index}>
          {item}
        </li>
      ));

      return block.ordered ? (
        <ol className="art-list art-list--ordered">{items}</ol>
      ) : (
        <ul className="art-list">{items}</ul>
      );
    }

    case 'steps':
      return <Steps items={block.items} />;

    case 'defs':
      return (
        <dl className="help-defs">
          {block.items.map((item, index) => (
            <div className="help-def" key={index}>
              <dt className="help-def-term">{item.term}</dt>
              <dd className="help-def-desc">{item.desc}</dd>
            </div>
          ))}
        </dl>
      );

    case 'note':
      /* İkon kutu İÇİNDE değil, doğrudan zemine oturuyor — DESIGN.md §3'ün
         kesinleşmiş kararı. Notun zemini ve sol hattı kabın kendisinde. */
      return (
        <aside className="art-note">
          <RiInformationLine className="art-note-icon" size={18} aria-hidden="true" />
          <div className="art-note-text">
            {block.title ? <p className="art-note-title">{block.title}</p> : null}
            <p className="art-note-body">{block.text}</p>
          </div>
        </aside>
      );

    case 'link': {
      const target = helpLinkHref(block.to);

      return (
        <p className="help-link-row">
          <span className="help-link-text">{block.text}</span>
          {/* Dış adres düz `<a>` ile basılmak ZORUNDA: `@/i18n/navigation`
              içindeki `Link` mutlak adrese dil öneki eklemiyor. */}
          {target.external ? (
            <a className="help-link" href={target.href} target="_blank" rel="noreferrer">
              {block.label}
              <RiArrowRightUpLine className="help-link-arrow" size={16} aria-hidden="true" />
            </a>
          ) : (
            <Link className="help-link" href={target.href}>
              {block.label}
              <RiArrowRightUpLine className="help-link-arrow" size={16} aria-hidden="true" />
            </Link>
          )}
        </p>
      );
    }
  }
}

/**
 * Adım dizisi.
 *
 * 🔴 Sıra sayısı DOLU BİR DAİRE İÇİNDE DEĞİL: sessiz bir üst etiket
 * ("ADIM 2/4"). Rakam rozeti bu üründe kullanılmıyor — bir adım numarası
 * bilgi taşır, dikkat çekmesi gereken şey adımın kendisidir.
 *
 * Etiketteki iki sayı `t`den değil buradan geliyor: mesaj dosyasına
 * "{current}/{total}" kalıbı koymak, çeviride bölü işaretinin kaybolabileceği
 * bir yer açardı ve kalıp iki dilde de aynı.
 */
function Steps({ items }: { items: readonly { title: string; text: string }[] }) {
  const t = useTranslations('help');

  return (
    <motion.ol
      className="help-steps"
      variants={stepsV}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
    >
      {items.map((item, index) => (
        <motion.li className="help-step" key={item.title} variants={stepV}>
          <span className="help-step-label">
            {t('step')} {index + 1}/{items.length}
          </span>
          <h3 className="help-step-title">{item.title}</h3>
          <p className="help-step-text">{item.text}</p>
        </motion.li>
      ))}
    </motion.ol>
  );
}
